import { lazyWithReload } from "src/shared/services/lazyWithReload/lazyWithReload";
import { RouteObject } from "react-router-dom";
import RouteProtector from "src/routes/RouteProtector";
import { DashboardSectionEnum } from "src/violationCase/types";

const ViolationCaseDetails = lazyWithReload(
  () => import("src/violationCase/features/details/ViolationCaseDetails"),
);

const FineCalculator = lazyWithReload(
  () => import("src/violationCase/features/FineCalculator/FineCalculator"),
);

const CasesDashboard = lazyWithReload(
  () => import("src/violationCase/features/CasesDashboard/CasesDashboard"),
);

const SupervisoryTeamViolationApplications = lazyWithReload(
  () =>
    import(
      "src/violationCase/features/SupervisoryTeamViolationApplications/SupervisoryTeamViolationApplications"
    ),
);

const ViolationForm = lazyWithReload(
  () => import("src/violationCase/features/ViolationCreateForm/ViolationForm"),
);

const Payment = lazyWithReload(
  () => import("src/violationCase/features/CompanyPayment/Payment"),
);

const FinalizeFineWithCorrection = lazyWithReload(
  () =>
    import(
      "src/violationCase/features/details/ui/FinalizeFineWithCorrection/FinalizeFineWithCorrection"
    ),
);

export const violationPaths: RouteObject = {
  path: "/violation",
  children: [
    ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
      ? [
          {
            path: "newCase",
            element: (
              <RouteProtector allowedUsers={["isChamber"]}>
                <ViolationForm
                  isEditMode={false}
                  isComplaintMode={false}
                  isSampleInspectionMode={false}
                  isEditDraftMode={false}
                />
              </RouteProtector>
            ),
          },
          {
            path: "case/:caseId/editDraft",
            element: (
              <RouteProtector allowedUsers={["isChamber"]}>
                <ViolationForm
                  isEditMode={false}
                  isComplaintMode={false}
                  isSampleInspectionMode={false}
                  isEditDraftMode={true}
                />
              </RouteProtector>
            ),
          },
          {
            path: "case/:caseId/edit",
            element: (
              <RouteProtector allowedUsers={["isOrgAdmin"]}>
                <ViolationForm
                  isEditMode={true}
                  isComplaintMode={false}
                  isSampleInspectionMode={false}
                  isEditDraftMode={false}
                />
              </RouteProtector>
            ),
          },
          {
            path: "case/newCaseFromComplaint/:complaintId",
            element: (
              <RouteProtector allowedUsers={["isChamber"]}>
                <ViolationForm
                  isEditMode={false}
                  isComplaintMode={true}
                  isSampleInspectionMode={false}
                  isEditDraftMode={false}
                />
              </RouteProtector>
            ),
          },
          {
            path: "case/newCaseFromSampleInspection/:sampleCompanyCaseId",
            element: (
              <RouteProtector allowedUsers={["isChamber"]}>
                <ViolationForm
                  isEditMode={false}
                  isComplaintMode={false}
                  isSampleInspectionMode={true}
                  isEditDraftMode={false}
                />
              </RouteProtector>
            ),
          },
          {
            path: "case/:caseId/details",
            element: (
              <RouteProtector
                allowedUsers={[
                  "isChamber",
                  "isBusiness",
                  "isGGEAdmin",
                  "isSystemAdmin",
                  "isSampleInitiator",
                  "isSupervisorAssignee",
                ]}
              >
                <ViolationCaseDetails />
              </RouteProtector>
            ),
          },
          {
            path: "case/:caseId/finalizeFineWithCorrection",
            element: (
              <RouteProtector allowedUsers={["isChamber"]}>
                <FinalizeFineWithCorrection />
              </RouteProtector>
            ),
          },
          {
            path: "company",
            children: [
              {
                path: "casesDashboard",
                element: (
                  <RouteProtector allowedUsers={["isBusiness"]}>
                    <CasesDashboard />
                  </RouteProtector>
                ),
              },
            ],
          },
          {
            path: "fine",
            children: [
              {
                path: "calculation",
                element: (
                  <RouteProtector allowedUsers={["isChamber"]}>
                    <FineCalculator />
                  </RouteProtector>
                ),
              },
            ],
          },
          {
            path: "chamber",
            children: [
              {
                path: "casesDashboard",
                element: (
                  <RouteProtector allowedUsers={["isOrgAdmin"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.CHAMBER_ADMIN_ACTIVE}
                      mode={DashboardSectionEnum.CHAMBER_ADMIN_ACTIVE}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "assignChamberAdminDashboard",
                element: (
                  <RouteProtector allowedUsers={["isOrgAdmin"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.ADMIN}
                      mode={DashboardSectionEnum.ADMIN}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "myChamberAdminCasesDashboard",
                element: (
                  <RouteProtector allowedUsers={["isOrgAdmin"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.ADMIN_ASSIGNED}
                      mode={DashboardSectionEnum.ADMIN_ASSIGNED}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "assignChamberUserDashboard",
                element: (
                  <RouteProtector allowedUsers={["isOrgAdmin"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.ADMIN_UNASSIGNED}
                      mode={DashboardSectionEnum.ADMIN_UNASSIGNED}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "myChamberUserCasesDashboard",
                element: (
                  <RouteProtector allowedUsers={["isChamberOnly"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.EMPLOYEE}
                      mode={DashboardSectionEnum.EMPLOYEE}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "myChamberUserCompletedCasesDashboard",
                element: (
                  <RouteProtector allowedUsers={["isChamberOnly"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.EMPLOYEE_COMPLETED}
                      mode={DashboardSectionEnum.EMPLOYEE_COMPLETED}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "pastViolationCases",
                element: (
                  <RouteProtector allowedUsers={["isOrgAdmin"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.CHAMBER_ADMIN_COMPLETED}
                      mode={DashboardSectionEnum.CHAMBER_ADMIN_COMPLETED}
                    />
                  </RouteProtector>
                ),
              },
              {
                path: "casesDashboardByCompany/:companyId",
                element: (
                  <RouteProtector allowedUsers={["isChamber"]}>
                    <CasesDashboard
                      key={DashboardSectionEnum.CHAMBER_SEARCH_BY_COMPANY_ID}
                      mode={DashboardSectionEnum.CHAMBER_SEARCH_BY_COMPANY_ID}
                    />
                  </RouteProtector>
                ),
              },
            ],
          },
          {
            path: "admin",
            children: [
              {
                path: "casesDashboard",
                element: (
                  <RouteProtector
                    allowedUsers={[
                      "isSystemAdmin",
                      "isSampleInitiator",
                      "isGGEAdmin",
                    ]}
                  >
                    <CasesDashboard
                      key={DashboardSectionEnum.SAMPLE_INITIATOR}
                      mode={DashboardSectionEnum.SAMPLE_INITIATOR}
                    />
                  </RouteProtector>
                ),
              },
            ],
          },
          {
            path: "supervisoryTeamViolationApplications",
            element: (
              <RouteProtector allowedUsers={["isSupervisorAssignee"]}>
                <SupervisoryTeamViolationApplications />
              </RouteProtector>
            ),
          },
          {
            path: "case/:caseId/payment",
            element: (
              <RouteProtector allowedUsers={["isBusiness"]}>
                <Payment />
              </RouteProtector>
            ),
          },
        ]
      : []),
  ],
};
